/**
 * Tags Selector Component
 * Multi-select for RBAC tags per organisation profile
 */

import { Label } from '@/components/ui/label';
import { MultiSelectPopover } from '@/shared/components/MultiSelectPopover';
import { useIsInternalStaff } from '@/hooks/useSession';
import { Loader2 } from 'lucide-react';
import { useRoles } from '../hooks/useUsers';
import {
  ATEK_ORG_ID,
  ATEK_ORG_BASE_TAGS,
  CUSTOMER_ACCOUNT_TAGS,
  CUSTOMER_CALIBRATION_TAGS,
  getOrgFeatureFlags,
} from '../utils/constants';

interface TagsSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  organisationId: string;
  disabled?: boolean;
}

export function TagsSelector({
  selectedTags,
  onChange,
  organisationId,
  disabled,
}: TagsSelectorProps) {
  const rolesQuery = useRoles();
  // `isAtekOrg` describes the *target user's* organisation (a domain field on
  // the user being edited — kept). `isAtekUser` describes the *logged-in*
  // user — migrated to RBAC: any internal ATEK staff member.
  const isAtekOrg = organisationId === ATEK_ORG_ID;
  const isAtekUser = useIsInternalStaff();
  const featureFlags = getOrgFeatureFlags();

  // Build available tags based on org type
  const getAvailableTags = (): string[] => {
    if (isAtekOrg) {
      // ATEK org: base tags + custom roles from API
      const customRoles = (rolesQuery.data || [])
        .map((role) => role.roleId)
        .filter((roleId) => !ATEK_ORG_BASE_TAGS.includes(roleId));
      return [...ATEK_ORG_BASE_TAGS, ...customRoles];
    }

    const tags = new Set<string>();

    if (isAtekUser) {
      for (const tag of CUSTOMER_ACCOUNT_TAGS) {
        tags.add(tag);
      }
    }

    if (featureFlags.customerManagedCalibration) {
      for (const tag of CUSTOMER_CALIBRATION_TAGS) {
        tags.add(tag);
      }
    }

    return [...tags];
  };

  const availableTags = getAvailableTags();

  if (rolesQuery.isLoading && isAtekOrg) {
    return (
      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tags...
        </div>
      </div>
    );
  }

  if (availableTags.length === 0) {
    return (
      <div className="space-y-2">
        <Label>Tags</Label>
        <p className="text-sm text-muted-foreground py-2">No tags available for this organisation</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>Tags</Label>
      <MultiSelectPopover
        value={selectedTags}
        options={availableTags.map((tag) => ({
          id: tag,
          label: tag,
        }))}
        onChange={onChange}
        placeholder="Select tags..."
        searchPlaceholder="Search tags..."
        emptyMessage="No tags available for this organisation"
        noMatchesMessage="No matching tags"
        disabled={disabled}
        showSelectAll
        selectAllLabel="Select all tags"
      />
      {selectedTags.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selectedTags.length} selected
        </p>
      )}
    </div>
  );
}
